/**
 * What the Conversation tab says about a Worker's persistent AI session.
 * Pure formatting — no I/O.
 */

export interface PersistentAiSessionView {
  readonly sessionId: string;
  readonly runtimeKey: string;
  readonly state: string;
  readonly createdAt: number;
  readonly lastActivityAt: number;
  readonly supported: boolean;
  readonly unsupportedReason: string | null;
  /** #688 context meter as the node reported it; null when this node did not report one. */
  readonly context?: PersistentContextView | null;
  /** #688: the context this one replaced (rollover or owner Start Fresh Context); null if none. */
  readonly continuedFrom?: PersistentContextLineageView | null;
}

/** #688: old -> new conversational context, as the node reported it. */
export interface PersistentContextLineageView {
  readonly fromSessionId: string;
  readonly reason: 'context_budget' | 'manual';
  readonly acknowledged: boolean;
}

/** Only a well-formed lineage from the node is shown; anything else is not reported. */
export function persistentLineageFromRaw(raw: unknown): PersistentContextLineageView | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const value = raw as Record<string, unknown>;
  const from = value['fromSessionId'];
  const reason = value['reason'];
  if (typeof from !== 'string' || from.trim() === '') return null;
  if (reason !== 'context_budget' && reason !== 'manual') return null;
  return { fromSessionId: from.trim().slice(0, 64), reason, acknowledged: value['acknowledged'] === true };
}

/** The owner-facing lineage line: where this context came from and whether it took the handoff. */
export function persistentLineageLine(lineage: PersistentContextLineageView | null | undefined): string | null {
  if (lineage === null || lineage === undefined) return null;
  const why = lineage.reason === 'manual' ? 'Fresh context you started' : 'Fresh context at the context limit';
  const ack = lineage.acknowledged
    ? 'continued the same chain from the durable handoff'
    : 'did not acknowledge the handoff yet - check the terminal';
  return `${why} · replaced ${lineage.fromSessionId.slice(0, 8)} · ${ack}`;
}

/** #688: how full this conversation is, on the basis the node states (measured or estimated). */
export interface PersistentContextView {
  readonly state: 'unknown_ceiling' | 'healthy' | 'growing' | 'prepare_handoff' | 'rotate_at_boundary' | 'hard_stop';
  readonly basis: 'measured' | 'estimated';
  readonly label: string;
}

const CONTEXT_STATES: ReadonlySet<string> = new Set([
  'unknown_ceiling', 'healthy', 'growing', 'prepare_handoff', 'rotate_at_boundary', 'hard_stop',
]);

/** Only a well-formed meter from the node is shown; anything else is not reported. */
export function persistentContextFromRaw(raw: unknown): PersistentContextView | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const value = raw as Record<string, unknown>;
  const state = value['state'];
  const basis = value['basis'];
  const label = value['label'];
  if (typeof state !== 'string' || !CONTEXT_STATES.has(state)) return null;
  if (basis !== 'measured' && basis !== 'estimated') return null;
  if (typeof label !== 'string' || label.trim() === '') return null;
  return { state: state as PersistentContextView['state'], basis, label: label.trim().slice(0, 200) };
}

/** The owner-facing line for the context meter, with what Brunel will do about it. */
export function persistentContextLine(context: PersistentContextView | null | undefined): string | null {
  if (context === null || context === undefined) return null;
  const next = context.state === 'prepare_handoff' ? ' · handoff kept ready'
    : context.state === 'rotate_at_boundary' ? ' · fresh context at the next safe boundary'
      : context.state === 'hard_stop' ? ' · near its limit: large turns wait for the fresh context'
        : '';
  return `AI context: ${context.label}${next}`;
}

const RUNTIME_ACRONYMS = new Set(['ai', 'api', 'cli', 'cpu', 'gpu', 'llm', 'mcp', 'pty', 'rpc', 'ui']);

export function persistentRuntimeLabel(runtimeKey: string): string {
  const parts = runtimeKey.trim().split(/[-_\s]+/).filter((part) => part.length > 0);
  if (parts.length === 0) return runtimeKey;
  return parts.map((part) => {
    const lower = part.toLowerCase();
    if (RUNTIME_ACRONYMS.has(lower)) return lower.toUpperCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }).join(' ');
}

export function persistentStateLabel(state: string): string {
  switch (state) {
    case 'starting':
      return 'Starting';
    case 'ready':
      return 'Ready';
    case 'busy':
      return 'Busy';
    case 'waiting_for_input':
      return 'Waiting for input';
    case 'error':
      return 'Error';
    case 'closed':
      return 'Closed';
    default:
      return state;
  }
}

function formatWhen(ms: number, nowMs: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return 'unknown';
  const delta = Math.max(0, nowMs - ms);
  if (delta < 60_000) return 'just now';
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)} min ago`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)} h ago`;
  return new Date(ms).toLocaleString();
}

/** One short block for the Conversation tab header. */
export function persistentSessionSummary(
  view: PersistentAiSessionView | null,
  nowMs: number,
): { readonly title: string; readonly lines: readonly string[] } {
  if (view === null) {
    return {
      title: 'Persistent AI session',
      lines: ['No persistent AI session on this Worker yet.'],
    };
  }
  if (!view.supported) {
    return {
      title: 'Persistent AI session',
      lines: [
        `Runtime: ${persistentRuntimeLabel(view.runtimeKey)}`,
        view.unsupportedReason ?? 'Not supported for persistent reuse.',
      ],
    };
  }
  return {
    title: 'Persistent AI session',
    lines: [
      `Runtime: ${persistentRuntimeLabel(view.runtimeKey)}`,
      `State: ${persistentStateLabel(view.state)}`,
      `Started: ${formatWhen(view.createdAt, nowMs)}`,
      `Last activity: ${formatWhen(view.lastActivityAt, nowMs)}`,
      ...(persistentContextLine(view.context) === null ? [] : [persistentContextLine(view.context)!]),
    ],
  };
}

/**
 * One `persistent-ai-state` broadcast, if it belongs to this Worker.
 *
 * The node's registry keys a session by the workforce session id (older nodes: the Worker
 * id). A payload for anyone else, or one missing the fields that identify it, is ignored
 * rather than guessed at. Pure, so the Terminal's session details and the old Conversation
 * tab read a broadcast the same way.
 */
export function persistentAiStateFromBroadcast(
  payload: unknown,
  keys: { readonly workforceSessionId: string | null; readonly workerId: string },
  nowMs: number,
): PersistentAiSessionView | null {
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const raw = payload as Record<string, unknown>;
  if (typeof raw['workerId'] !== 'string' || typeof raw['runtimeKey'] !== 'string') return null;
  if (raw['workerId'] !== keys.workforceSessionId && raw['workerId'] !== keys.workerId) return null;
  return {
    sessionId: typeof raw['sessionId'] === 'string' ? raw['sessionId'] : '',
    runtimeKey: raw['runtimeKey'],
    state: typeof raw['state'] === 'string' ? raw['state'] : 'ready',
    createdAt: typeof raw['createdAt'] === 'number' ? raw['createdAt'] : nowMs,
    lastActivityAt: typeof raw['lastActivityAt'] === 'number' ? raw['lastActivityAt'] : nowMs,
    supported: raw['supported'] !== false,
    unsupportedReason: typeof raw['unsupportedReason'] === 'string' ? raw['unsupportedReason'] : null,
    context: persistentContextFromRaw(raw['context']),
    continuedFrom: persistentLineageFromRaw(raw['continuedFrom']),
  };
}

/**
 * The AI session lines for Terminal -> Session details.
 *
 * The node sends this state when it changes, so "nothing heard" since this view opened is
 * Unknown - not "no session".
 */
export function terminalAiSessionLines(
  view: PersistentAiSessionView | null,
  nowMs: number,
): readonly string[] {
  if (view === null) return ['AI session: not reported by the node since this view opened.'];
  if (!view.supported) {
    return [
      `AI session: ${persistentRuntimeLabel(view.runtimeKey)} · not kept open between turns`,
      view.unsupportedReason ?? 'Not supported for persistent reuse.',
    ];
  }
  return [
    `AI session: ${persistentRuntimeLabel(view.runtimeKey)} · ${persistentStateLabel(view.state)}`,
    `Started ${formatWhen(view.createdAt, nowMs)} · last activity ${formatWhen(view.lastActivityAt, nowMs)}`,
    ...(persistentContextLine(view.context) === null ? [] : [persistentContextLine(view.context)!]),
    ...(persistentLineageLine(view.continuedFrom) === null ? [] : [persistentLineageLine(view.continuedFrom)!]),
  ];
}
